/* ============================================================================
   BILOVA motion engine — vanilla JS, no dependencies.
   Everything is progressive: with JS off or reduced-motion on, the page is
   still complete and readable.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var root = document.documentElement;

  // Tell CSS that JS is alive, so it may hide things it will reveal again.
  root.classList.add('blv-js');

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* -- shared rAF scroll bus: one listener, many subscribers -------------- */
  var scrollSubs = [];
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      var y = window.pageYOffset;
      var vh = window.innerHeight;
      for (var i = 0; i < scrollSubs.length; i++) {
        try { scrollSubs[i](y, vh); } catch (e) { /* never let one break the rest */ }
      }
    });
  }
  function subscribe(fn) { scrollSubs.push(fn); }

  /* -- 1. page curtain + fade-in ----------------------------------------- */
  function initCurtain() {
    if (reduced) return;
    var main = document.querySelector('main') || document.body;
    main.classList.add('blv-pagefade');

    var curtain = document.createElement('div');
    curtain.className = 'blv-curtain';
    curtain.innerHTML = '<div class="blv-curtain__mark">BILOVA</div>';
    document.body.appendChild(curtain);

    function lift() {
      curtain.classList.add('is-gone');
      main.classList.add('is-ready');
      setTimeout(function () { curtain.remove(); }, 900);
    }
    if (document.readyState === 'complete') setTimeout(lift, 250);
    else window.addEventListener('load', function () { setTimeout(lift, 250); });
    // Safety: never trap the page behind the curtain.
    setTimeout(lift, 2600);

    // Fade out on internal navigation.
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a');
      if (!a) return;
      var href = a.getAttribute('href') || '';
      if (a.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey) return;
      if (!href || href.charAt(0) === '#' || href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) return;
      if (a.hostname && a.hostname !== window.location.hostname) return;
      document.body.classList.add('blv-leaving');
    });
  }

  /* -- 2. scroll reveal --------------------------------------------------- */
  function initReveal() {
    var targets = document.querySelectorAll('[data-blv-reveal]');
    if (!targets.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var i = Number(el.getAttribute('data-blv-index') || 0);
        el.style.setProperty('--blv-delay', i * 110 + 'ms');
        el.classList.add('is-in');
        io.unobserve(el); // reveal once
      });
    }, { rootMargin: '0px 0px -70px 0px', threshold: 0.01 });
    targets.forEach(function (el) { io.observe(el); });
  }

  /* -- 3. word-by-word headline ------------------------------------------ */
  function initWords() {
    var els = document.querySelectorAll('[data-blv-words]');
    if (!els.length) return;
    els.forEach(function (el) {
      if (el.dataset.blvWordsDone) return;
      el.dataset.blvWordsDone = '1';
      // Split into words, but keep a whole <em>…</em> as ONE unit wrapped from
      // the outside — nesting reveal spans inside the em breaks its
      // background-clip gradient (the text goes transparent with no gradient
      // behind it, i.e. invisible).
      var html = el.innerHTML.trim();
      var parts = html.split(/(<em>[\s\S]*?<\/em>|<[^>]+>)/g);
      var out = '';
      var w = 0;
      function wrap(tok) {
        out += '<span class="blv-word"><span style="--blv-wdelay:' + (w * 70) + 'ms">' + tok + '</span></span>';
        w++;
      }
      parts.forEach(function (part) {
        if (!part) return;
        if (/^<em>/i.test(part)) { wrap(part); return; }
        if (part.charAt(0) === '<') { out += part; return; }
        part.split(/(\s+)/).forEach(function (tok) {
          if (!tok.trim()) { out += tok; return; }
          wrap(tok);
        });
      });
      el.innerHTML = out;
      el.classList.add('blv-words');
      if (reduced) { el.classList.add('is-in'); return; }
      if (!('IntersectionObserver' in window)) { el.classList.add('is-in'); return; }
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
      }, { threshold: 0.2 });
      io.observe(el);
    });
  }

  /* -- 4. count-up stats -------------------------------------------------- */
  function initCounters() {
    var els = document.querySelectorAll('[data-blv-count]');
    if (!els.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.textContent = el.getAttribute('data-blv-count') + (el.getAttribute('data-blv-suffix') || ''); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        io.unobserve(el);
        var target = parseFloat(el.getAttribute('data-blv-count')) || 0;
        var suffix = el.getAttribute('data-blv-suffix') || '';
        var start = null, DUR = 1700;
        function tick(ts) {
          if (start === null) start = ts;
          var p = Math.min((ts - start) / DUR, 1);
          var eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
          el.textContent = Math.round(target * eased) + suffix;
          if (p < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* -- 5. marquee doubling ------------------------------------------------ */
  function initMarquee() {
    document.querySelectorAll('[data-blv-marquee]').forEach(function (track) {
      if (track.dataset.blvDoubled) return;
      track.innerHTML += track.innerHTML; // CSS scrolls to -50%
      track.dataset.blvDoubled = '1';
    });
  }

  /* -- 6. hero motes ------------------------------------------------------ */
  function initMotes() {
    if (reduced) return;
    var host = document.querySelector('[data-blv-motes]');
    if (!host) return;
    var n = window.innerWidth < 700 ? 8 : 22;   // fewer nodes on phones
    var frag = document.createDocumentFragment();
    for (var i = 0; i < n; i++) {
      var d = document.createElement('span');
      var size = 2 + Math.random() * 5;
      d.className = 'blv-mote';
      d.style.width = size + 'px';
      d.style.height = size + 'px';
      d.style.left = (Math.random() * 100) + '%';
      d.style.bottom = '-8%';
      d.style.animationDuration = (11 + Math.random() * 16) + 's';
      d.style.animationDelay = (-Math.random() * 20) + 's';
      d.style.setProperty('--blv-drift', (Math.random() * 120 - 60) + 'px');
      frag.appendChild(d);
    }
    host.appendChild(frag);
  }

  /* -- 7. scroll progress + floating jar ---------------------------------- */
  function initScrollChrome() {
    var bar = document.querySelector('[data-blv-progress]');
    var jar = document.querySelector('[data-blv-floatjar]');
    var dock = document.getElementById('shop-jar-dock');
    if (!bar && !jar) return;
    subscribe(function (y, vh) {
      if (bar) {
        var docH = document.documentElement.scrollHeight - vh;
        bar.style.transform = 'scaleX(' + (docH > 0 ? Math.min(y / docH, 1) : 0) + ')';
      }
      if (jar) {
        var past = y > vh * 0.55;
        var docked = false;
        if (dock) docked = dock.getBoundingClientRect().top < vh * 0.85;
        jar.classList.toggle('is-visible', past && !docked);
        jar.classList.toggle('is-docked', docked);
      }
    });
  }

  /* -- 8. scroll-driven scenes + parallax --------------------------------- */
  function initScenes() {
    if (reduced) return;
    var scenes = [].slice.call(document.querySelectorAll('[data-blv-scene]'));
    var plx = [].slice.call(document.querySelectorAll('[data-blv-parallax]'));
    if (!scenes.length && !plx.length) return;
    subscribe(function (y, vh) {
      scenes.forEach(function (s) {
        var r = s.getBoundingClientRect();
        // 0 when the scene's top hits the viewport bottom, 1 when its bottom hits the top
        var total = r.height + vh;
        var p = (vh - r.top) / total;
        s.style.setProperty('--blv-p', Math.max(0, Math.min(1, p)).toFixed(4));
      });
      plx.forEach(function (el) {
        var speed = parseFloat(el.getAttribute('data-blv-parallax')) || 0.18;
        var r = el.getBoundingClientRect();
        var centre = r.top + r.height / 2 - vh / 2;
        el.style.setProperty('--blv-py', (-centre * speed).toFixed(1) + 'px');
      });
    });
  }

  /* -- 9. card cursor glow ------------------------------------------------ */
  function initCardGlow() {
    if (reduced || window.matchMedia('(pointer: coarse)').matches) return;
    document.querySelectorAll('.blv-card').forEach(function (card) {
      if (!card.querySelector('.blv-card__glow')) {
        var g = document.createElement('span');
        g.className = 'blv-card__glow';
        card.appendChild(g);
      }
      card.addEventListener('mousemove', function (e) {
        var r = card.getBoundingClientRect();
        card.style.setProperty('--blv-mx', (e.clientX - r.left) + 'px');
        card.style.setProperty('--blv-my', (e.clientY - r.top) + 'px');
      });
    });
  }

  /* -- 10. cursor glow ---------------------------------------------------- */
  function initCursor() {
    if (reduced || window.matchMedia('(pointer: coarse)').matches) return;
    var c = document.createElement('div');
    c.className = 'blv-cursor';
    document.body.appendChild(c);
    var x = -100, y = -100, cx = -100, cy = -100;
    document.addEventListener('mousemove', function (e) { x = e.clientX; y = e.clientY; }, { passive: true });
    (function loop() {
      cx += (x - cx) * 0.18; cy += (y - cy) * 0.18;
      c.style.transform = 'translate3d(' + (cx - 13) + 'px,' + (cy - 13) + 'px,0)';
      requestAnimationFrame(loop);
    })();
    document.addEventListener('mouseover', function (e) {
      var hot = e.target.closest && e.target.closest('a,button,.blv-card');
      c.classList.toggle('is-hot', !!hot);
    });
  }

  /* -- 11. FAQ accordion (animated, a11y-friendly) ------------------------ */
  function initAccordion() {
    document.querySelectorAll('.blv-acc').forEach(function (acc) {
      var q = acc.querySelector('.blv-acc__q');
      var a = acc.querySelector('.blv-acc__a');
      if (!q || !a) return;
      q.setAttribute('aria-expanded', 'false');
      q.addEventListener('click', function () {
        var open = acc.classList.toggle('is-open');
        q.setAttribute('aria-expanded', open ? 'true' : 'false');
        a.style.height = open ? a.firstElementChild.offsetHeight + 'px' : '0px';
      });
    });
  }


  /* -- 12. hero interactivity: mouse parallax + scroll link + steam --------- */
  function initHero() {
    var hero = document.querySelector('.blv-hero');
    if (!hero) return;

    // Steam wisps drifting off the jar
    if (!reduced) {
      var wrap = hero.querySelector('.blv-hero__jarwrap') || hero;
      for (var s = 0; s < 3; s++) {
        var w = document.createElement('span');
        w.className = 'blv-steam';
        w.style.left = (18 + s * 26) + '%';
        w.style.top = '6%';
        w.style.animationDelay = (-s * 2.3) + 's';
        wrap.appendChild(w);
      }
    }

    // Cursor parallax: -1..1 from hero centre, eased toward the target.
    if (!reduced && !window.matchMedia('(pointer: coarse)').matches) {
      var tx = 0, ty = 0, cx = 0, cy = 0, running = false;
      hero.addEventListener('mousemove', function (e) {
        var r = hero.getBoundingClientRect();
        tx = (e.clientX - r.left) / r.width * 2 - 1;
        ty = (e.clientY - r.top) / r.height * 2 - 1;
        if (!running) { running = true; requestAnimationFrame(ease); }
      }, { passive: true });
      hero.addEventListener('mouseleave', function () { tx = 0; ty = 0; });
      function ease() {
        cx += (tx - cx) * 0.09;
        cy += (ty - cy) * 0.09;
        hero.style.setProperty('--blv-hx', cx.toFixed(4));
        hero.style.setProperty('--blv-hy', cy.toFixed(4));
        if (Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001) requestAnimationFrame(ease);
        else running = false;
      }
    }

    // Scroll progress across the hero, 0..1
    if (!reduced) {
      subscribe(function (y, vh) {
        var h = hero.offsetHeight || vh;
        hero.style.setProperty('--blv-hp', Math.max(0, Math.min(1, y / h)).toFixed(4));
      });
    }
  }

  /* -- 13. magnetic buttons + click ripple --------------------------------- */
  function initMagnetic() {
    var fine = !window.matchMedia('(pointer: coarse)').matches;
    document.querySelectorAll('.blv-btn').forEach(function (btn) {
      btn.classList.add('blv-magnetic');
      if (!reduced && fine) {
        btn.addEventListener('mousemove', function (e) {
          var r = btn.getBoundingClientRect();
          var mx = e.clientX - r.left - r.width / 2;
          var my = e.clientY - r.top - r.height / 2;
          btn.style.transform = 'translate(' + mx * 0.28 + 'px,' + my * 0.4 + 'px)';
        });
        btn.addEventListener('mouseleave', function () { btn.style.transform = ''; });
      }
      // Ripple works on touch too.
      btn.addEventListener('click', function (e) {
        if (reduced) return;
        var r = btn.getBoundingClientRect();
        var d = document.createElement('span');
        d.className = 'blv-ripple';
        d.style.left = (e.clientX - r.left) + 'px';
        d.style.top = (e.clientY - r.top) + 'px';
        d.style.width = d.style.height = Math.max(r.width, r.height) / 4 + 'px';
        btn.appendChild(d);
        setTimeout(function () { d.remove(); }, 800);
      });
    });
  }


  /* -- 14. sticky mobile buy bar ------------------------------------------ */
  function initBuyBar() {
    var bar = document.querySelector('[data-blv-buybar]');
    if (!bar) return;
    var anchor = document.querySelector('.blv-shop, .blv-prod, form[action*="/cart/add"]');
    subscribe(function (y, vh) {
      // Show once the user is past the first screen, hide near the footer.
      var docH = document.documentElement.scrollHeight;
      var nearEnd = (y + vh) > (docH - 240);
      bar.classList.toggle('is-up', y > vh * 0.9 && !nearEnd);
    });
  }


  /* -- 15. product-card size switcher -------------------------------------- */
  function initSizeSwitch() {
    document.querySelectorAll('.blv-prod').forEach(function (card) {
      var chips = card.querySelectorAll('[data-blv-size]');
      if (!chips.length) return;
      var priceEl   = card.querySelector('[data-blv-price]');
      var compareEl = card.querySelector('[data-blv-compare]');
      var fromEl    = card.querySelector('[data-blv-from]');
      var cta       = card.querySelector('[data-blv-cta]');
      var line      = card.querySelector('[data-blv-priceline]');

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          chips.forEach(function (c) { c.classList.remove('is-active'); c.setAttribute('aria-pressed','false'); });
          chip.classList.add('is-active');
          chip.setAttribute('aria-pressed','true');

          if (priceEl) priceEl.textContent = chip.dataset.price || priceEl.textContent;

          // compare-at may not exist for every variant, so create/remove as needed
          var cmp = chip.dataset.compare;
          if (compareEl) {
            if (cmp) { compareEl.textContent = cmp; compareEl.style.display = ''; }
            else compareEl.style.display = 'none';
          } else if (cmp && line && priceEl) {
            var s2 = document.createElement('s');
            s2.setAttribute('data-blv-compare','');
            s2.textContent = cmp;
            line.insertBefore(s2, priceEl);
            compareEl = s2;
          }

          if (fromEl) fromEl.style.display = 'none';   // an exact size is now chosen
          if (cta && chip.dataset.url) cta.setAttribute('href', chip.dataset.url);

          if (!reduced && priceEl) {
            priceEl.animate(
              [{ transform: 'translateY(-6px)', opacity: .35 }, { transform: 'none', opacity: 1 }],
              { duration: 320, easing: 'cubic-bezier(.16,1,.3,1)' }
            );
          }
        });
      });
    });
  }


  /* -- 16. lab-report note toggle ----------------------------------------- */
  function initLab() {
    document.querySelectorAll('[data-blv-lab]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var note = document.getElementById('blv-lab-note');
        if (!note) return;
        var open = note.hasAttribute('hidden');
        if (open) note.removeAttribute('hidden'); else note.setAttribute('hidden','');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  onReady(function () {
    initCurtain();
    initLab();
    initSizeSwitch();
    initBuyBar();
    initHero();
    initMagnetic();
    initWords();
    initReveal();
    initCounters();
    initMarquee();
    initMotes();
    initScrollChrome();
    initScenes();
    initCardGlow();
    initCursor();
    initAccordion();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();
  });
})();
